import { useToast, Button } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import axios from 'axios';
import FileSaver from 'file-saver';
import React, { useCallback, useState } from 'react';

import { useGetDeliverableArtifactInstanceDownloadLazyQueryGQLType } from '../requests/deliverables.gql.generated';
import { IconButton } from 'packages/common-frontend/components/IconButton';
import { Icon } from 'packages/common-frontend/components/icons/fontAwesome/Icon';
import {
  successToastConfig,
  errorToastId,
  errorToastConfig,
} from 'packages/common-frontend/constants';

export const DownloadButton: React.FC<{
  fileName: string;
  artifactInstanceId: string;
  iconOnly?: boolean;
}> = ({ fileName, artifactInstanceId, iconOnly }) => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [
    getDeliverableArtifactInstanceDownload,
  ] = useGetDeliverableArtifactInstanceDownloadLazyQueryGQLType({
    fetchPolicy: 'no-cache',
    onCompleted: (response) => {
      if (response && response.deliverableArtifactInstanceDownload) {
        const {
          deliverableArtifactInstanceDownload: downloadResponse,
        } = response;
        if (downloadResponse.__typename === 'ArtifactInstanceDownloadUrl') {
          const url = downloadResponse.signedUrl;
          axios({
            url,
            method: 'GET',
            responseType: 'blob',
          })
            .then((res) => {
              const fileBlob = new Blob([res.data], {
                type: res.data.type,
              });
              FileSaver.saveAs(fileBlob, fileName);
              successToastConfig.title = `Successfully downloaded ${fileName}`;
              toast(successToastConfig);
              setLoading(false);
            })
            .catch((err) => {
              setLoading(true);
              if (!toast.isActive(errorToastId)) {
                errorToastConfig.title = `Error downloading ${fileName}`;
                toast(errorToastConfig);
              }
              Sentry.captureException(err);
            });
        }
        if (downloadResponse.__typename === 'ArtifactInstanceDownloadObject') {
          const object = downloadResponse.object;
          const blob = new Blob([JSON.stringify(object)], {
            type: 'application/json',
          });
          const url = window.URL.createObjectURL(blob);

          const tag = document.createElement('a');
          tag.setAttribute('href', url);
          tag.setAttribute('download', `${fileName}.json`);
          tag.click();
          setLoading(false);
        }
      }
    },
  });

  const onClick = useCallback(() => {
    setLoading(true);
    Sentry.addBreadcrumb({
      category: 'ui.click',
      message: `Clicked download for ${fileName} v4 deliverables [iconOnly: ${
        iconOnly ?? false
      }]`,
      level: Sentry.Severity.Info,
    });
    getDeliverableArtifactInstanceDownload({
      variables: {
        artifactInstanceId,
      },
    });
  }, [
    artifactInstanceId,
    fileName,
    getDeliverableArtifactInstanceDownload,
    iconOnly,
  ]);

  if (iconOnly) {
    return (
      <IconButton
        icon={<Icon icon="download" color="blue.400" />}
        isLoading={loading}
        color="blue.400"
        ariaLabel="download-file"
        label="Download File"
        onClick={onClick}
      />
    );
  }

  return (
    <Button
      isLoading={loading}
      color="blue.400"
      leftIcon={<Icon icon="download" color="blue.400" boxSize={4} />}
      variant="ghost"
      fontSize="sm"
      py="2px"
      px="8px"
      onClick={onClick}
    >
      Download
    </Button>
  );
};
